import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import backgroundImage from '../assets/world-map.png';

// Scroll Animation Wrapper
const FadeInSection = ({ children, delay = 0, direction = 'up' }) => {
  const [isVisible, setVisible] = useState(false);
  const domRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) setVisible(true);
      });
    }, { threshold: 0.1 }); 
    
    const currentRef = domRef.current;
    if (currentRef) observer.observe(currentRef);
    return () => { if (currentRef) observer.unobserve(currentRef); };
  }, []);

  const translateClass = direction === 'left' ? 'translate-x-10' : direction === 'right' ? '-translate-x-10' : 'translate-y-20';

  return (
    <div
      ref={domRef}
      className={`transition-all duration-1000 ease-out transform ${
        isVisible ? 'opacity-100 translate-y-0 translate-x-0' : `opacity-0 ${translateClass}`
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

// Auto Counting Numbers Component
const Counter = ({ end, duration = 1000, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const domRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) setIsVisible(true);
    });
    if (domRef.current) observer.observe(domRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    let start = 0;
    const increment = end / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.ceil(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [isVisible, end, duration]);

  return <span ref={domRef}>{count}{suffix}</span>;
};

const Home = () => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const services = [
    { title: "B2B E-Commerce", desc: "The Spacezant solution harnesses B2B integrations, the most powerful wholesale tools available today.", category: "Enterprise", img: "https://images.unsplash.com/photo-1661956602116-aa6865609028?auto=format&fit=crop&w=600&q=80" },
    { title: "B2C Marketplaces", desc: "No more waiting. Direct to consumer solutions that build beautiful storefronts in minutes.", category: "Consumer", img: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=600&q=80" },
    { title: "Retail Customers", desc: "Empowering local retail businesses to go global with cutting edge pos & tracking systems.", category: "Retail", img: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=600&q=80" },
    { title: "Brand Promotions", desc: "Global reach & brand visibility campaigns to maximize your international conversions.", category: "Marketing", img: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&w=600&q=80" },
    { title: "Every Category", desc: "From food to electronics, clothes to gadgets. Everything managed under one roof seamlessly.", category: "Diverse", img: "https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?auto=format&fit=crop&w=600&q=80" },
    { title: "Intl. Delivery", desc: "Fast, secure and tracked worldwide shipping networks ensuring zero unnecessary delays.", category: "Logistics", img: "https://images.unsplash.com/photo-1580674285054-bed31e145f59?auto=format&fit=crop&w=600&q=80" },
  ];

  const shippingPartners = [
    { name: 'FedEx', color: 'text-purple-600' },
    { name: 'DHL', color: 'text-yellow-500' },
    { name: 'BlueDart', color: 'text-blue-600' },
    { name: 'UPS', color: 'text-amber-700' },
    { name: 'Aramex', color: 'text-red-500' },
    { name: 'Delhivery', color: 'text-pink-600' },
    { name: 'Ecom Express', color: 'text-emerald-600' },
  ];
  const duplicatedPartners = [...shippingPartners, ...shippingPartners, ...shippingPartners];

  const paymentPartners = [
    { name: 'Razorpay', color: 'text-blue-600' },
    { name: 'PhonePe', color: 'text-purple-600' },
    { name: 'Paytm', color: 'text-sky-500' },
    { name: 'Stripe', color: 'text-indigo-500' },
    { name: 'PayPal', color: 'text-blue-800' },
    { name: 'Google Pay', color: 'text-gray-700' },
    { name: 'Amazon Pay', color: 'text-orange-500' },
  ];
  const duplicatedPayments = [...paymentPartners, ...paymentPartners, ...paymentPartners];

  return (
    <div className="bg-[#f8fafc] min-h-screen font-sans overflow-x-hidden">
      
      {/* 1. HERO SECTION (Portrait Blur & Shining Map) */}
      <section className="relative min-h-screen overflow-hidden flex flex-col justify-center bg-[#0f172a] pt-20">
        <div 
          className={`absolute inset-0 z-0 transition-opacity duration-[2000ms] ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
          style={{ animation: isLoaded ? 'kenBurnsEffect 30s ease-in-out infinite' : 'none' }}
        >
          {/* Map image with Shining filter */}
          <img 
            src={backgroundImage} 
            alt="Spacezant Global Map Background" 
            className="w-full h-full object-cover object-center filter brightness-110 saturate-125"
          />
          {/* Natural Gradient for text visibility */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#0f172a]/20 via-[#0f172a]/60 to-[#0f172a]"></div>
        </div>

        <div className="max-w-7xl mx-auto w-full px-4 sm:px-8 md:px-16 py-12 z-10">
          
          {/* UPDATED: BIG CENTERED WORK IN PROGRESS BANNER */}
          <div 
            className={`w-full flex justify-center mb-12 transform transition-all duration-1000 delay-100 ease-out ${
              isLoaded ? 'translate-y-0 opacity-100' : '-translate-y-10 opacity-0'
            }`}
          >
            <div className="inline-flex items-center space-x-4 bg-white/10 backdrop-blur-xl border border-white/20 rounded-[2.5rem] px-8 py-5 shadow-2xl animate-pulse ring-1 ring-white/10">
              <span className="text-3xl sm:text-4xl">🏗️</span>
              <div className="text-center sm:text-left">
                <p className="text-[#e85d04] font-black text-base sm:text-lg uppercase tracking-[0.3em]">Work In Progress</p>
                <p className="text-white text-sm sm:text-base font-medium opacity-90">Our global platform is launching very soon!</p>
              </div>
            </div>
          </div>

          <div className="max-w-3xl mx-auto text-center md:text-left md:mx-0">
            <div 
              className={`backdrop-blur-[3px] bg-white/5 p-6 md:p-10 rounded-[2.5rem] border border-white/10 shadow-2xl transform transition-all duration-[1200ms] delay-200 ease-out w-full ${
                isLoaded ? 'translate-x-0 opacity-100' : '-translate-x-24 opacity-0'
              }`}
            >
              <div className="inline-block border border-[#e85d04]/40 bg-[#e85d04]/20 backdrop-blur-sm rounded-full px-4 py-1.5 text-xs font-bold text-[#e85d04] mb-6 uppercase tracking-wider">
                Global Commerce
              </div>
              <h1 className="text-4xl sm:text-5xl md:text-7xl font-extrabold leading-tight sm:leading-[1.1] mb-6 tracking-tight text-white">
                Innovate the future <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#e85d04] to-orange-400">
                  of E-commerce
                </span><br />
                worldwide.
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-gray-200 max-w-2xl leading-relaxed mb-6 font-medium mx-auto md:mx-0">
                We provide cutting-edge solutions for global marketplace integration, local seller empowerment, and seamless cross-border logistics.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 2. WHO WE ARE */}
      <section className="py-20 sm:py-24 px-4 sm:px-6 md:px-16 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <FadeInSection direction="left">
              <div className="inline-block border border-[#e85d04]/30 bg-[#e85d04]/10 rounded-full px-4 py-1.5 text-xs font-bold text-[#e85d04] mb-4 uppercase tracking-wider">
                Who We Are
              </div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-slate-900 leading-tight mb-8 text-left">
                Collaborate, Promote & <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#e85d04] to-orange-500">Excel Together</span>
              </h2>
            </FadeInSection>
            <div className="space-y-4 sm:space-y-6">
              {[
                { title: "All-in-One Platform", text: "Everything you need is in one platform. From food to electronics, clothes to gadgets.", icon: "M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" },
                { title: "Brand Integration", text: "Our mission is to seamlessly integrate domestic and international brands globally.", icon: "M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" },
                { title: "Quick & Safe Delivery", text: "We deliver quick and safe, ensuring trust and reliability at every step.", icon: "M13 10V3L4 14h7v7l9-11h-7z" }
              ].map((item, idx) => (
                <FadeInSection delay={idx * 150} direction="left" key={idx}>
                  <div className="flex p-3 sm:p-4 rounded-xl hover:bg-slate-50 transition-colors group">
                    <div className="flex-shrink-0 mr-4 sm:mr-5 mt-1">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-slate-100 group-hover:bg-[#e85d04]/10 rounded-full flex items-center justify-center text-slate-400 group-hover:text-[#e85d04] transition-all duration-300">
                        <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={item.icon}></path></svg>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-1 group-hover:text-[#e85d04] transition-colors">{item.title}</h3>
                      <p className="text-slate-600 leading-relaxed text-xs sm:text-sm">{item.text}</p>
                    </div>
                  </div>
                </FadeInSection>
              ))}
            </div>
          </div>
          <FadeInSection direction="right">
            <div className="relative group mt-10 lg:mt-0 px-4 sm:px-0">
              <div className="absolute -bottom-4 -left-2 sm:-bottom-6 sm:-left-6 bg-white p-4 sm:p-5 rounded-2xl shadow-xl border border-gray-100 z-10">
                <p className="text-2xl sm:text-3xl font-extrabold text-slate-900"><Counter end={10} suffix="K+" /></p>
                <p className="text-[10px] sm:text-sm font-semibold text-slate-500 uppercase tracking-wider">Global Brands</p>
              </div>
              <div className="rounded-2xl overflow-hidden shadow-2xl bg-white border border-gray-100 animate-float">
                <img src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=800&q=80" alt="Team" className="w-full h-auto object-cover" />
                <div className="h-2 w-full bg-gradient-to-r from-[#e85d04] to-orange-400 absolute bottom-0 left-0"></div>
              </div>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* 3. OUR SERVICES */}
      <section className="py-20 sm:py-24 px-4 sm:px-6 md:px-16 bg-[#0f172a]">
        <div className="max-w-7xl mx-auto z-10">
          <FadeInSection>
            <div className="text-center max-w-3xl mx-auto mb-16 sm:mb-20">
              <h4 className="text-[#e85d04] font-bold tracking-widest uppercase text-sm mb-3">Our Services</h4>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white mb-6">Innovative Solutions</h2>
            </div>
          </FadeInSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-12">
            {services.map((srv, idx) => (
              <FadeInSection delay={idx * 100} key={idx}>
                <div className="relative w-full h-[400px] sm:h-[450px] md:h-[480px] group cursor-pointer overflow-hidden">
                  <div className="absolute top-0 left-0 w-[85%] h-[80%] rounded-3xl overflow-hidden">
                    <img src={srv.img} alt={srv.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  </div>
                  <div className="absolute bottom-0 right-0 w-[85%] bg-white rounded-3xl p-5 sm:p-8 shadow-2xl transition-transform duration-500 group-hover:-translate-y-2">
                    <p className="text-[#e85d04] text-[10px] sm:text-xs font-bold uppercase mb-2 tracking-widest">{srv.category}</p>
                    <h3 className="text-lg sm:text-2xl font-bold text-slate-900 mb-2 sm:mb-4">{srv.title}</h3>
                    <p className="text-slate-500 text-xs sm:text-sm leading-relaxed mb-4 sm:mb-6 line-clamp-2 sm:line-clamp-3">{srv.desc}</p>
                    <div className="flex items-center text-[#e85d04] font-bold text-[10px] sm:text-xs uppercase tracking-wider">
                      <span className="w-8 h-[2px] bg-[#e85d04] mr-3 transition-all duration-300 group-hover:w-12"></span> READ MORE
                    </div>
                  </div>
                </div>
              </FadeInSection>
            ))}
          </div>
          <div className="text-center mt-12 sm:mt-16">
            <Link to="/services" className="bg-transparent border border-[#e85d04] text-white px-8 sm:px-10 py-3 rounded-full font-bold hover:bg-[#e85d04] transition-all text-sm sm:text-base inline-block">All Services →</Link>
          </div>
        </div>
      </section>

      {/* 4. PARTNERS (Left & Right Marquees) */}
      <section className="bg-white py-16 sm:py-20 px-4 sm:px-6 md:px-16 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <h4 className="text-center text-slate-400 font-bold uppercase text-[10px] sm:text-xs mb-8 sm:mb-10 tracking-widest">Delivery Partners</h4>
          <div className="relative w-full flex overflow-hidden group mb-16 sm:mb-20">
            <div className="flex animate-marquee whitespace-nowrap space-x-12 sm:space-x-16 items-center group-hover:pause">
              {duplicatedPartners.map((p, i) => <span key={i} className={`text-3xl sm:text-4xl md:text-5xl font-extrabold mx-6 sm:mx-8 opacity-40 hover:opacity-100 transition-opacity ${p.color}`}>{p.name}</span>)}
            </div>
          </div>
          <h4 className="text-center text-slate-400 font-bold uppercase text-[10px] sm:text-xs mb-8 sm:mb-10 tracking-widest">Payment Gateways</h4>
          <div className="relative w-full flex overflow-hidden group">
            <div className="flex animate-marquee-reverse whitespace-nowrap space-x-12 sm:space-x-16 items-center group-hover:pause">
              {duplicatedPayments.map((p, i) => <span key={i} className={`text-3xl sm:text-4xl md:text-5xl font-extrabold mx-6 sm:mx-8 opacity-40 hover:opacity-100 transition-opacity ${p.color}`}>{p.name}</span>)}
            </div>
          </div>
        </div>
      </section>

      {/* 5. CONTRIBUTIONS */}
      <section className="bg-white pb-20 sm:pb-24 px-4 sm:px-6 md:px-16">
        <div className="max-w-7xl mx-auto">
          <FadeInSection delay={200}>
            <div className="bg-slate-50 border border-slate-100 rounded-3xl p-6 sm:p-10 md:p-16 shadow-sm relative overflow-hidden text-center">
              <h2 className="text-2xl sm:text-3xl md:text-5xl font-extrabold text-slate-900 mb-4">Our Contributions</h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mt-8 sm:mt-12">
                <div className="p-2 sm:p-4"><div className="text-2xl sm:text-4xl font-extrabold text-[#e85d04] mb-2"><Counter end={150} suffix="+" /></div><div className="text-slate-600 font-bold uppercase text-[10px] sm:text-xs">Countries</div></div>
                <div className="p-2 sm:p-4"><div className="text-2xl sm:text-4xl font-extrabold text-[#e85d04] mb-2"><Counter end={10} suffix="K+" /></div><div className="text-slate-600 font-bold uppercase text-[10px] sm:text-xs">Brands</div></div>
                <div className="p-2 sm:p-4"><div className="text-2xl sm:text-4xl font-extrabold text-[#e85d04] mb-2"><Counter end={1} suffix="M+" /></div><div className="text-slate-600 font-bold uppercase text-[10px] sm:text-xs">Products</div></div>
                <div className="p-2 sm:p-4"><div className="text-2xl sm:text-4xl font-extrabold text-[#e85d04] mb-2"><Counter end={99} suffix="%" /></div><div className="text-slate-600 font-bold uppercase text-[10px] sm:text-xs">Safe</div></div>
              </div>
            </div>
          </FadeInSection>
        </div>
      </section>

      <style>{`
        @keyframes kenBurnsEffect { 0% { transform: scale(1); } 50% { transform: scale(1.05); } 100% { transform: scale(1); } }
        @keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        @keyframes marquee-reverse { 0% { transform: translateX(-50%); } 100% { transform: translateX(0); } }
        @keyframes floatEffect { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-15px); } }
        .animate-marquee { animation: marquee 25s linear infinite; width: max-content; }
        .animate-marquee-reverse { animation: marquee-reverse 25s linear infinite; width: max-content; }
        .animate-float { animation: floatEffect 5s ease-in-out infinite; }
        .group-hover\\:pause:hover { animation-play-state: paused; }
      `}</style>
    </div>
  );
};

export default Home;